// ═══════════════════════════════════════════════════════════════════════
// THE round countdown derivation — pure, tested.
//
// Extracted verbatim from ShowRaceStatus's `getCountdownInfo` so the crash
// centre display (CrashMultiplierDisplay) and the status pill derive their
// remaining-seconds from the SAME event/config source — one clock, two
// surfaces, no drift. ShowRaceStatus now calls this too.
//
// All times are wall-clock except a hard round's heard count (`hard`, and its
// span, on `performance.now()`); windows come from the game config via
// getBettingTimeWindow / getSettledTimeWindow (callers pass the resolved
// seconds so this module stays pure and store-free).
// ═══════════════════════════════════════════════════════════════════════

// Structural event shape — matches RaceEvent without importing the domain
// type (keeps the module dependency-free for tests).
export interface RaceEventTimeLike {
  eventType: string
  eventTimestamp: string | Date
}

export interface RaceCountdownInfo {
  // Betting/settled phases: remaining seconds. Started/ended: elapsed
  // duration. (Same semantics as ShowRaceStatus.)
  current: number
  // Total window the countdown runs over (0 = not a countdown phase).
  animationDuration: number
  // Elapsed fraction of the window, 0..100.
  progress: number
}

export interface RaceCountdownInputs {
  raceEvents: readonly RaceEventTimeLike[]
  nowMs: number
  // getBettingTimeWindow(game) — open + closing window, seconds.
  bettingWindowSec: number
  // getSettledTimeWindow(game) — post-settle gap, seconds.
  settledWindowSec: number
  // ShowRaceStatus's 1Hz-ticked settled/cancelled override — takes
  // precedence in those phases so its pill and interval stay in lockstep.
  finalCountdown?: number | null
  // CRASH ONLY. When set, race_ended is treated as the between-rounds anchor
  // (the "Next round" countdown starts there) instead of showing the round's
  // elapsed duration. The crash engine inserts race_ended post_hold after the
  // viewer's crash frame and then fires race_settled immediately after it, so
  // anchoring there removes the dead beat without the ring jumping.
  //
  // MUST stay opt-in: this module drives every game's pill, and the marble
  // engine still leaves a 2s race_ended -> race_settled gap. Turning it on
  // fleet-wide would start every marble game's between-rounds countdown 2s
  // early and strand it at 0.
  betweenRoundsAnchorsOnEnded?: boolean
  // A HARD round's betting count (hard close rev 4, DESIGN §6): to the board's
  // lock, then the short count of X, both on this page's own arrival clock
  // (`utils/hardCloseClock.ts`). Absent or null: today's count, from the
  // race_ready stamp.
  hard?: {
    current: number
    animationDuration: number
    progress: number
    /** Its window on `performance.now()`, ms (`HardCountdown`), for a ring drawn between ticks. */
    startAt?: number
    endAt?: number
  } | null
  // A HARD round's W and X, for a page that holds no heard moment to count
  // from (`hard` null: it came in after race_ready). Its count then reads the
  // round's own window off race_ready's stamp, as today's count reads the
  // game's configured one: to the lock (d + W − X), and once race_starting is
  // on the round, the short count of X to the cut (d + W). Absent or null, or
  // no usable W: today's count. Only the classic status pill passes it.
  hardWindow?: { windowS: number | null; lockLeadS: number } | null
}

/**
 * The window a countdown runs over, so a ring can be drawn at any moment
 * rather than at a tick: `perf` on `performance.now()` (a hard round's heard
 * count), `wall` on `Date.now()` (a count off a row's stamp).
 */
export interface CountdownSpan {
  clock: 'perf' | 'wall'
  startAt: number
  endAt: number
}

const tsMs = (e: RaceEventTimeLike): number => new Date(e.eventTimestamp).getTime()

// Latest event by timestamp (non-mutating twin of raceUtils.getLatestEvent).
const latestOf = (events: readonly RaceEventTimeLike[]): RaceEventTimeLike | undefined => {
  let latest: RaceEventTimeLike | undefined
  for (const e of events) {
    if (latest === undefined || tsMs(e) > tsMs(latest)) latest = e
  }
  return latest
}

const findEvent = (
  events: readonly RaceEventTimeLike[],
  type: string
): RaceEventTimeLike | undefined => events.find((e) => e.eventType === type)

/** How far through its span `now` is, 0..100, continuous: the ring's fill. */
export function spanProgressAt(span: CountdownSpan, now: number): number {
  const totalMs = span.endAt - span.startAt
  if (!(totalMs > 0)) return 100
  return Math.min(100, Math.max(0, ((now - span.startAt) / totalMs) * 100))
}

// A count over a span, as `hardCountdown` counts: whole seconds left, rounded up.
function countOver(span: CountdownSpan, now: number): RaceCountdownInfo {
  return {
    current: Math.ceil(Math.max(0, span.endAt - now) / 1000),
    animationDuration: (span.endAt - span.startAt) / 1000,
    progress: spanProgressAt(span, now)
  }
}

// `hardWindow`'s count off race_ready's stamp (see RaceCountdownInputs), or
// null when the round has no usable hard window.
function stampedHardSpan(
  i: RaceCountdownInputs,
  raceReadyEvent: RaceEventTimeLike
): CountdownSpan | null {
  const w = i.hardWindow
  if (typeof w?.windowS !== 'number') return null
  const d = tsMs(raceReadyEvent)
  const lockAt = d + (w.windowS - w.lockLeadS) * 1000
  return findEvent(i.raceEvents, 'race_starting')
    ? { clock: 'wall', startAt: lockAt, endAt: d + w.windowS * 1000 }
    : { clock: 'wall', startAt: d, endAt: lockAt }
}

// The row the "Next round" countdown is anchored on: the EARLIEST
// between-rounds marker present. On crash, race_ended fires first (post_hold
// after the viewer's crash) and race_settled follows, so anchoring on
// race_ended starts the ring as soon as the player has seen the crash AND
// keeps it from jumping backwards when race_settled arrives. Cancelled rounds
// have no race_ended and use their own event.
function betweenRoundsAnchor(
  i: RaceCountdownInputs,
  latestEvent: RaceEventTimeLike
): RaceEventTimeLike | undefined {
  return latestEvent.eventType === 'race_cancelled'
    ? findEvent(i.raceEvents, 'race_cancelled')
    : (i.betweenRoundsAnchorsOnEnded ? findEvent(i.raceEvents, 'race_ended') : undefined) ??
        findEvent(i.raceEvents, 'race_settled')
}

// The "Next round" countdown, shared by the race_settled / race_cancelled
// phases and (crash only) the race_ended phase — one implementation so the two
// entry points can never drift.
function betweenRoundsCountdown(
  i: RaceCountdownInputs,
  latestEvent: RaceEventTimeLike,
  settledWindowSec: number,
  nowMs: number,
  finalCountdown: number | null
): RaceCountdownInfo {
  if (finalCountdown !== null) {
    return {
      current: finalCountdown,
      animationDuration: settledWindowSec,
      progress: Math.min(((settledWindowSec - finalCountdown) / settledWindowSec) * 100, 100)
    }
  }

  const relevantEvent = betweenRoundsAnchor(i, latestEvent)

  if (!relevantEvent) return { current: 0, animationDuration: settledWindowSec, progress: 0 }

  const eventTime = tsMs(relevantEvent)
  const elapsed = Math.floor((nowMs - eventTime) / 1000)
  const remaining = Math.max(settledWindowSec - elapsed, 0)

  return {
    current: remaining,
    animationDuration: settledWindowSec,
    progress: Math.min((elapsed / settledWindowSec) * 100, 100)
  }
}

export function getRaceCountdownInfo(i: RaceCountdownInputs): RaceCountdownInfo {
  const latestEvent = latestOf(i.raceEvents)
  if (!latestEvent) return { current: 0, animationDuration: 0, progress: 0 }

  const now = i.nowMs
  const TOTAL_COUNTDOWN_TIME = i.bettingWindowSec
  const SETTLED_COUNTDOWN_TIME = i.settledWindowSec
  const finalCountdown = i.finalCountdown ?? null

  switch (latestEvent.eventType) {
    case 'race_ready':
    case 'race_starting': {
      if (i.hard) {
        return {
          current: i.hard.current,
          animationDuration: i.hard.animationDuration,
          progress: i.hard.progress
        }
      }
      const raceReadyEvent = findEvent(i.raceEvents, 'race_ready')
      if (!raceReadyEvent)
        return { current: 0, animationDuration: TOTAL_COUNTDOWN_TIME, progress: 0 }

      const stamped = stampedHardSpan(i, raceReadyEvent)
      if (stamped) return countOver(stamped, now)

      const startTime = tsMs(raceReadyEvent)
      const elapsed = Math.floor((now - startTime) / 1000)
      const remaining = Math.max(TOTAL_COUNTDOWN_TIME - elapsed, 0)

      return {
        current: remaining,
        animationDuration: TOTAL_COUNTDOWN_TIME,
        progress: Math.min((elapsed / TOTAL_COUNTDOWN_TIME) * 100, 100)
      }
    }

    case 'race_started': {
      const raceStartEvent = findEvent(i.raceEvents, 'race_started')
      if (!raceStartEvent) return { current: 0, animationDuration: 0, progress: 0 }
      const startTime = tsMs(raceStartEvent)
      const duration = Math.floor((now - startTime) / 1000)
      return {
        current: duration,
        animationDuration: 0,
        progress: 100
      }
    }

    case 'race_crashed':
    case 'race_ended': {
      // Crash only (betweenRoundsAnchorsOnEnded): race_ended IS the
      // between-rounds anchor, so fall through to the countdown below rather
      // than showing the round's elapsed duration. Every other game — and
      // race_crashed on any game — keeps the elapsed readout.
      //
      // NOTE: this used to be a second `case 'race_ended':` on the group
      // below. A duplicate case label is not a TS/JS error, and the first
      // match wins, so that branch was unreachable and race_ended rendered the
      // climb's elapsed seconds under a "Next round" label.
      if (latestEvent.eventType === 'race_ended' && i.betweenRoundsAnchorsOnEnded) {
        return betweenRoundsCountdown(i, latestEvent, SETTLED_COUNTDOWN_TIME, now, finalCountdown)
      }
      const raceStartEvent = findEvent(i.raceEvents, 'race_started')
      if (!raceStartEvent) return { current: 0, animationDuration: 0, progress: 0 }
      const raceEndEvent = findEvent(i.raceEvents, 'race_ended')
      const startTime = tsMs(raceStartEvent)
      const endTime = raceEndEvent ? tsMs(raceEndEvent) : tsMs(latestEvent)
      const duration = Math.floor((endTime - startTime) / 1000)
      return {
        current: duration,
        animationDuration: 0,
        progress: 100
      }
    }

    case 'race_settled':
    case 'race_cancelled':
      return betweenRoundsCountdown(i, latestEvent, SETTLED_COUNTDOWN_TIME, now, finalCountdown)

    default:
      return { current: 0, animationDuration: 0, progress: 0 }
  }
}

/**
 * The span the countdown of `getRaceCountdownInfo` runs over at this moment,
 * or null where it is not a countdown (the race running, before a round). The
 * same phases, rows and windows as `getRaceCountdownInfo`; its seconds stay
 * whole there, and a ring reads its fill off this span (`spanProgressAt`) at
 * every frame. A span holds until a row arrives or a hard round's lock moves
 * it to the count of X.
 */
export function getRaceCountdownSpan(i: RaceCountdownInputs): CountdownSpan | null {
  const latestEvent = latestOf(i.raceEvents)
  if (!latestEvent) return null

  switch (latestEvent.eventType) {
    case 'race_ready':
    case 'race_starting': {
      if (i.hard) {
        return i.hard.startAt !== undefined && i.hard.endAt !== undefined
          ? { clock: 'perf', startAt: i.hard.startAt, endAt: i.hard.endAt }
          : null
      }
      const raceReadyEvent = findEvent(i.raceEvents, 'race_ready')
      if (!raceReadyEvent) return null
      const d = tsMs(raceReadyEvent)
      return (
        stampedHardSpan(i, raceReadyEvent) ?? {
          clock: 'wall',
          startAt: d,
          endAt: d + i.bettingWindowSec * 1000
        }
      )
    }

    case 'race_ended':
    case 'race_settled':
    case 'race_cancelled': {
      if (latestEvent.eventType === 'race_ended' && !i.betweenRoundsAnchorsOnEnded) return null
      const anchor = betweenRoundsAnchor(i, latestEvent)
      if (!anchor) return null
      const t = tsMs(anchor)
      return { clock: 'wall', startAt: t, endAt: t + i.settledWindowSec * 1000 }
    }

    default:
      return null
  }
}

// ── Centre-display countdown source selection ────────────────────────────
// Which countdown (if any) the crash centre narrative shows for the current
// round phase:
//
//   betting    — race_ready / race_starting: "Place your bets" + the betting
//                ring (remaining of open+closing window).
//   next-round — race_ended / race_settled / race_cancelled: "Next round" +
//                the settled ring, mirroring the pill's between-rounds
//                countdown so the centre never goes blank between the
//                CRASHED-hold and the next betting window.
//   null       — every other phase (arming/live/crash own the stage; the
//                race_scheduled gap has no bounded countdown).
//
// Why race_ended and NOT race_crashed: race_ended is inserted post_hold AFTER
// the viewer's crash frame (engine race_ended_aftermath_seconds), so by then
// the player has seen the crash and the ring can start. race_crashed is the
// ENGINE's crash instant and reaches the client seconds BEFORE the crash
// reaches their stream — the very lag holdCrashPill exists to absorb. Starting
// a "next round" countdown there would announce the round was over while the
// player's video still showed it climbing.
export type CenterCountdownKind = 'betting' | 'next-round'

export function resolveCenterCountdown(
  latestEventType: string | undefined
): CenterCountdownKind | null {
  if (latestEventType === 'race_ready' || latestEventType === 'race_starting') return 'betting'
  if (
    latestEventType === 'race_ended' ||
    latestEventType === 'race_settled' ||
    latestEventType === 'race_cancelled'
  )
    return 'next-round'
  return null
}

// Seconds → display string (extracted from ShowRaceStatus.formatTime):
// plain seconds under a minute, m:ss from 60 up, NaN-safe.
export function formatCountdownSeconds(seconds: number): string {
  if (isNaN(seconds)) return '0'
  if (seconds >= 60) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }
  return seconds.toString()
}
